import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'

const schema = z.object({ token: z.string().optional() })

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: (s) => schema.parse(s),
  head: () => ({
    meta: [
      { title: 'Unsubscribe — BidNova Auctions' },
      { name: 'description', content: 'Manage your email preferences for BidNova Auctions.' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: UnsubscribePage,
})

function UnsubscribePage() {
  const { token } = useSearch({ from: '/unsubscribe' })
  const [state, setState] = useState<'loading' | 'ready' | 'already' | 'invalid' | 'done' | 'error'>('loading')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) { setState('invalid'); return }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) return setState('invalid')
        if (data.reason === 'already_unsubscribed') return setState('already')
        setState(data.valid ? 'ready' : 'invalid')
      })
      .catch(() => setState('error'))
  }, [token])

  const confirm = async () => {
    if (!token) return
    setBusy(true)
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json().catch(() => ({}))
      if (data.success) setState('done')
      else if (data.reason === 'already_unsubscribed') setState('already')
      else setState('error')
    } catch {
      setState('error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      <h1 className="font-display text-3xl text-gold-gradient">Email preferences</h1>
      {state === 'loading' && <p className="mt-6 text-sm text-muted-foreground">Checking your link…</p>}
      {state === 'invalid' && <p className="mt-6 text-sm text-muted-foreground">This unsubscribe link is invalid or has expired.</p>}
      {state === 'error' && <p className="mt-6 text-sm text-destructive">Something went wrong. Please try again.</p>}
      {state === 'already' && <p className="mt-6 text-sm text-muted-foreground">You're already unsubscribed. No further emails will be sent to this address.</p>}
      {state === 'ready' && (
        <>
          <p className="mt-6 text-sm text-muted-foreground">Confirm you'd like to unsubscribe from BidNova Auctions emails.</p>
          <Button onClick={confirm} disabled={busy} className="mt-6 bg-gold-gradient">
            {busy ? 'Working…' : 'Unsubscribe'}
          </Button>
        </>
      )}
      {state === 'done' && <p className="mt-6 text-sm">You've been unsubscribed. Sorry to see you go.</p>}
    </div>
  )
}
